import { Auth0ContextInterface, GetTokenSilentlyOptions } from "@auth0/auth0-react";
import { config, latestPunchesLimit } from "../constants"
import { GeneralException } from "../exception/general-exception";

export interface Work {
    project: Project,
    activeSprints: Array<number>,
    openPunch?: Punch
}

export interface Project {
    id: number,
    name: string,
    favorite: boolean,
    active: boolean,
    color?: string,
    editing?: boolean
}

export interface Punch {
    id?: number,
    start: string,
    end?: string,
    sprintId: number,
    workDescription: string
}

export interface PunchEntry extends Punch {
    project: Project
}

export interface Sprint {
    id: number,
    projectId: number
}

export class BackendService {

    getToken: (options: GetTokenSilentlyOptions) => Promise<string>;
    loginWithRedirect: Auth0ContextInterface['loginWithRedirect'];

    constructor(auth: Auth0ContextInterface) {
        const { getAccessTokenSilently, loginWithRedirect } = auth;
        this.getToken = getAccessTokenSilently;
        this.loginWithRedirect = loginWithRedirect;
    }

    async getProjects(): Promise<Array<Project>> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects`, await this.get());
        if (response.status === 200) {
            return response.json();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async addProject(project: Project): Promise<Project> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects`, await this.post({
            name: project.name,
            favorite: project.favorite,
            active: project.active,
            color: project.color
        }));

        if (response.status === 200) {
            return response.json();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async updateProject(project: Project): Promise<Project> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects`, await this.put(project));
        if (response.status === 200) {
            return response.json();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async deleteProject(project: Project): Promise<void> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects/${project.id}`, await this.delete());
        if (response.status === 200) {
            return Promise.resolve();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async getWorkSet(): Promise<Array<Work>> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/work-sets`, await this.get());
        if (response.status === 200) {
            return response.json()
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }

    }

    async punch(project: Project, punch: Punch): Promise<Punch> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects/${project.id}/sprints/${punch.sprintId}/punches`,
            await this.post(punch));
        if (response.status === 200) {
            return response.json();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async updatePunch(project: Project, punch: Punch): Promise<Punch> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects/${project.id}/sprints/${punch.sprintId}/punches/${punch.id}`,
            await this.post({
                start: punch.start,
                end: punch.end,
                workDescription: punch.workDescription
            }));
        if (response.status === 200) {
            return response.json()
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async getLatestPunches(): Promise<Array<PunchEntry>> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/latest-punches?limit=${latestPunchesLimit}`, await this.get());
        if (response.status === 200) {
            return response.json();
        } else {
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        }
    }

    async getLatestPunch(projectId: number, sprintId: number): Promise<PunchEntry | undefined> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects/${projectId}/sprints/${sprintId}/punches/latest`, await this.get());
        if (response.status === 200) {
            return response.json();
        } else if (response.status === 404) { // 404 is fine here, we might not have punches for this yet
            return Promise.resolve(undefined);
        } else { 
            return response.json().then(data => Promise.reject(new GeneralException(response.status, data)));
        } 
    }

    async getPunchesByDate(startDate: string, endDate: string): Promise<Array<PunchEntry>> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/punches-by-date?beginDate=${startDate}&endDate=${endDate}`,
            await this.get());
        if (response.status === 200) {
            return response.json();
        } else {
            return Promise.reject(response.status + ' returned');
        }
    }

    async createSprint(project: Project): Promise<Sprint> {
        let response = await fetch(`${config.url.BACKEND_URL}/private/projects/${project.id}/sprints`, await this.post());
        if (response.status === 200) {
            return response.json();
        } else {
            let data = await response.json();
            return Promise.reject(new GeneralException(response.status, data));
        }
    }

    private async readAllToken(): Promise<string> {
        try {
            return await this.getToken({
                authorizationParams: {
                    audience: config.auth0.audience,
                    scope: "read:all"
                }
            });
        } catch (error) {
            // Session/refresh token is no longer valid (e.g. expired after being idle).
            // Send the user back through login instead of leaving the app in a
            // silently broken, logged-in-looking-but-empty state.
            await this.loginWithRedirect({
                appState: { returnTo: window.location.pathname }
            });
            throw error;
        }
    }

    private async get() {
        return {
            method: "GET",
            headers: {
                "content-type": "application/json",
                "Authorization": `Bearer ${await this.readAllToken()}`
            }
        }
    }

    private async post(data?: any) {
        return {
            method: "POST",
            headers: {
                "content-type": "application/json",
                "Authorization": `Bearer ${await this.readAllToken()}`
            },
            body: data ? JSON.stringify(data) : ""
        }
    }

    private async put(data: any) {
        return {
            method: "PUT",
            headers: {
                "content-type": "application/json",
                "Authorization": `Bearer ${await this.readAllToken()}`
            },
            body: JSON.stringify(data)
        }
    }

    private async delete(data?: any) {
        return {
            method: "DELETE",
            headers: {
                "content-type": "application/json",
                "Authorization": `Bearer ${await this.readAllToken()}`
            },
            body: data ? JSON.stringify(data) : ""
        }
    }
}
